<template>
  <div class="screen-cotainer">
    <v-layout column>
      <v-flex class="title-screen" xs12>
        <span>Attendance requests</span>
      </v-flex>
      <v-flex x4>
        <div class="title-description attendee-table-description">
          Below you can see the list of event attendance requests, which you can approve or deny. If
          the setting is on, corresponding mail will be sent to approved or denied attendees.
        </div>
      </v-flex>
      <div v-if="!$apollo.loading" class="attendee_table">
        <table-filters
          v-if="!showAlternateHeader"
          :visible-fields.sync="visibleFields"
          :all-fields="allFields"
          :initial-filters="{ quickSearch, ...filters }"
          :attendee-fields="attendeeFields"
          :categories="categories"
          :extra-modules="extraModules"
          :event-id="eventId"
          @quickSearch="handleQuickSearch"
          @filters="handleFilters"
        />
        <selected-requests-header
          v-else
          :selected="selected"
          :user="user"
          :attendees="attendees"
          :total-accepted-attendee-count="totalAcceptedAttendeeCount"
          @clear="selected = []"
          @accept="approveAttendees"
          @deny="showDenyDialog = true"
        />
        <div
          v-if="attendees.length === 0"
          class="no-attendees-message-container border__left border__right"
        >
          <span class="no-attendees-message">No matching records found.</span>
        </div>
        <table-list
          v-else
          :event-id="eventId"
          :organization-slug="organizationSlug"
          :selected.sync="selected"
          :attendees="attendees"
          :visible-fields="visibleFields"
          :attendee-fields="attendeeFields"
          :pagination="pagination"
          @click="goTo(routes.organizer.editAccreditation, { accreditationId: $event })"
          @sort="handleSort($event)"
        />
        <table-pagination
          :pagination="pagination"
          :total-attendees="totalAttendeeCount"
          @change="handlePagination($event)"
          @changePerPage="handlePagination($event)"
        />
      </div>
    </v-layout>
    <delete-dialog
      :content="`Are you sure you want to deny selected requests? This operation cannot be undone.`"
      :on-delete="denyAttendees"
      :show.sync="showDenyDialog"
      title="Deny attendees"
      action-message="Deny"
    />
    <simple-snackbar
      :open.sync="onPrintSnackbarOpen"
      :message-prop.sync="onPrintSnackbarMessage"
      :color="onPrintSnackbarColor"
    />
  </div>
</template>

<script>
import { difference } from 'lodash'

import TableFilters from './attendee-table/filters.vue'
import TableList from './attendee-table/list.vue'
import TablePagination from './attendee-table/pagination.vue'
import SelectedRequestsHeader from './attendee-table/selected-requests-header.vue'

import { objectToArray, serverURL, getVisibleFields, setVisibleFields } from '../../utils'
import { GET_EVENT_DETAILS, GET_EVENT_ACCREDITATIONS_ONLY } from '../../graphql/event.graphql'
import { BULK_CREATE_PRINT, PRINT_ALL } from '../../graphql/print.graphql'
import { getAttendeeByBarcode } from '../../http'
import DeleteDialog from './../shared/dialogs/delete-dialog.vue'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { listOrderComparator } from './attendee-fields'
import { approveAccreditations, denyAccreditations } from '../../http'

export default {
  name: 'accreditations-list-container',
  components: {
    TableFilters,
    TableList,
    TablePagination,
    SelectedRequestsHeader,
    DeleteDialog,
    SimpleSnackbar,
  },
  props: {
    eventId: String,
    user: Object,
    organizationSlug: String,
  },
  data() {
    const { pagination, quickSearch, filters } = this.getInitialFromQueryParams()

    return {
      attendees: [],
      totalAttendeeCount: 0,
      totalAcceptedAttendeeCount: 0,
      categories: [],
      attendeeFields: [],
      eventAttendeeFields: {},
      extraModules: {},
      selected: [],
      quickSearch,
      pagination,
      permission: '',
      url: '',
      snackbar: false,
      filters,
      onCopyInviteSnackbar: false,
      showDenyDialog: false,
      onPrintSnackbarOpen: false,
      onPrintSnackbarMessage: '',
      onPrintSnackbarColor: '',
      designSet: true,
      paginationLoading: false,
      managed: '',
      quickPrint: false,
      barcode: '',
      onBarcodeError: false,
      serverURL,
      visibleFields: [],
      allFields: [],
    }
  },
  computed: {
    showAlternateHeader() {
      return this.selected && this.selected.length > 0
    },
  },
  async mounted() {
    await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_DETAILS,
      variables() {
        return {
          eventId: this.eventId,
          status: 'ALLOWED',
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          const {
            getEvent: { builder, extraModules, categories, managed, attendeeFields, quickPrint },
            getAccreditationCount,
          } = data
          this.totalAttendeeCount = getAccreditationCount
          this.categories = categories || []
          this.attendeeFields = attendeeFields
          this.eventAttendeeFields = objectToArray(attendeeFields, 'name').sort(listOrderComparator)
          this.extraModules = extraModules || {}
          this.designSet = !!builder
          this.managed = managed
          this.quickPrint = quickPrint
          this.allFields = [...this.eventAttendeeFields.map(f => f.name)]
          if (this.extraModules.categoriesAndZones) {
            this.allFields = [...this.eventAttendeeFields.map(f => f.name), 'Category']
          }
          const localStorageFields = getVisibleFields(this.eventId)
          if (localStorageFields.length > 0) {
            if (difference(localStorageFields, this.allFields).length > 0) {
              // if there is a field in my local, and not in definition, it means that other user deleted it
              this.visibleFields = this.allFields
            } else {
              this.visibleFields = localStorageFields
            }
          } else {
            this.visibleFields = this.allFields
          }
          setVisibleFields(this.visibleFields, this.eventId)
        }
      },
    },
  },
  methods: {
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination

      await this.fetchAttendees(
        pagination,
        this.filters,
        typeof this.quickSearch === 'string' ? { quickSearch: this.quickSearch } : this.quickSearch,
      )
    },
    async handleFilters(filters) {
      this.addFiltersToRoute(filters)
      this.filters = filters

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleQuickSearch(quickSearch) {
      this.pagination = { ...this.pagination, page: 1 }
      this.addFiltersToRoute(quickSearch)
      this.quickSearch = quickSearch

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalAttendeeCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      this.addPaginationToRoute(this.pagination)

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending, quickSearch, ...fieldFilters } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 25,
          sortBy: sortBy,
          descending: descending === 'true' ? true : false,
        },
        quickSearch: quickSearch || '',
        filters: { ...(fieldFilters || {}), curatedInvitee: false },
      }
    },
    addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending,
        },
      })
    },
    addFiltersToRoute(filters) {
      const allFilters = { ...this.$route.query, ...filters }
      const filtersWithValueArray = Object.entries(allFilters).filter(f => f[1]) // remove entries with no value
      // const filtersWithValue = Object.fromEntries(filtersWithValueArray)
      const filtersWithValue = filtersWithValueArray.reduce((acc, e) => {
        acc[e[0]] = e[1]

        return acc
      }, {})

      this.$router.replace({
        query: {
          ...filtersWithValue,
        },
      })
    },
    async onBarcodeScanned() {
      const { data, errors } = await getAttendeeByBarcode(this.barcode, this.eventId)
      if (data && !errors) {
        const { id } = data.getAttendeeByBarcode
        this.goTo(this.routes.organizer.editAccreditation, { accreditationId: id })
      } else {
        this.onBarcodeError = true
      }
      this.barcode = ''
    },
    toArray(array) {
      return Array.isArray(array) ? array : [array]
    },
    areFiltersEmpty() {
      return Object.values(this.filters).some(f => f === undefined || f.length === 0)
    },
    checkTrialLimit() {
      return this.user.type === 'TRIAL' ? this.totalAttendeeCount >= 20 : false
    },
    async print() {
      const status = await this.$apollo.mutate({
        mutation: BULK_CREATE_PRINT,
        variables: {
          accreditationIds: this.selected,
          id: this.eventId,
        },
      })
      this.afterPrint(status.data.bulkCreatePrint)
    },
    afterPrint() {
      this.selected = []
      this.onPrintSnackbarOpen = true
      this.onPrintSnackbarMessage = 'We have sent you an email with your accreditations'
      this.onPrintSnackbarColor = 'primary'
    },
    async printAll() {
      this.$apollo.mutate({
        mutation: PRINT_ALL,
        variables: {
          id: this.eventId,
        },
      })
      this.afterPrint()
    },
    async fetchAttendees(pagination, filters, quickSearch) {
      this.paginationLoading = true
      const {
        data: { getEventAccreditations },
      } = await this.$apollo.query({
        query: GET_EVENT_ACCREDITATIONS_ONLY,
        variables: {
          eventId: this.eventId,
          status: 'PENDING',
          quickSearch: (quickSearch && quickSearch.quickSearch) || '',
          visibleFields: this.visibleFields,
          pagination: pagination || this.pagination,
          filters: filters || this.filters || {},
        },
      })
      if (!getEventAccreditations) {
        return
      }
      const { attendees, attendeeCount } = getEventAccreditations

      this.attendees = attendees.filter(a => !a.fields.curatedInvitee)
      this.totalAttendeeCount = parseInt(attendeeCount, 10)
      this.paginationLoading = false

      // get accepted attendees count
      const {
        data: { getEventAccreditations: result },
      } = await this.$apollo.query({
        query: GET_EVENT_ACCREDITATIONS_ONLY,
        variables: { eventId: this.eventId, status: 'ALLOWED' },
      })
      const { attendeeCount: totalAcceptedAttendeeCount } = result
      this.totalAcceptedAttendeeCount = parseInt(totalAcceptedAttendeeCount, 10)
    },
    async denyAttendees() {
      const variables = {
        attendeeIds: this.selected,
        eventId: this.eventId,
      }
      await denyAccreditations(variables)
      await this.fetchAttendees()
      this.totalAttendeeCount = this.totalAttendeeCount - this.selected.length
      this.selected = []
    },
    async approveAttendees() {
      const variables = {
        attendeeIds: this.selected,
        eventId: this.eventId,
      }
      await approveAccreditations(variables)
      await this.fetchAttendees()
      this.totalAttendeeCount = this.totalAttendeeCount - this.selected.length
      this.selected = []
    },
  },
}
</script>

<style scoped>
.attendee-table-description {
  max-width: 900px;
  margin-bottom: 28px;
}
.no-attendees-message-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
.no-attendees-message {
  color: #979797;
  padding: 20px;
}
.border__left {
  border-left: 2px solid #f5f5f5;
}
.border__right {
  border-right: 2px solid #f5f5f5;
}
ul {
  list-style-type: none;
}

.err-line {
  margin-bottom: 8px;
}

.err-line span {
  font-weight: bold;
}
@media screen and (max-width: 800px) {
  .screen-cotainer {
    margin-top: 1.5rem;
  }
  .title-screen {
    display: none;
  }
}
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
</style>
